import {
  Children,
  forwardRef,
  isValidElement,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import Input, { InputControlProps } from './Input'
import List from './List'
import Wrapper from './Wrapper'

type SelectProps<T = any> = {
  value: T
  onSelect: (value: T) => void
  children: ReactNode
  readOnly?: boolean
  disabled?: boolean
  direction?: 'top' | 'bottom'
  listStyle?: string
  listTestId?: string
  listContentTestId?: string
  arrowTestId?: string
} & Omit<InputControlProps, 'opened' | 'setOpen' | 'onSelect'>

/*
props: label, value, onSelect, hasError,
  noCursor (убирает возможность ввода текста),
  noArrow,
  другие стандартные для нативного инпута
 */
export const Select = forwardRef<HTMLInputElement, SelectProps>(
  (props, ref) => {
    const {
      onSelect,
      children,
      direction = 'bottom',
      listStyle,
      listTestId,
      listContentTestId,
      arrowTestId,
      ...inputProps
    } = props

    const [activeRow, setActiveRow] = useState(-1)
    const [opened, setOpened] = useState(false)

    const isDisabled = inputProps.disabled || inputProps.readOnly

    const open = (val: boolean) => {
      setOpened(!isDisabled && val)
    }

    const handleSelect = (value: unknown) => {
      open(false)
      if (onSelect) onSelect(value)
    }

    const handleSelectActiveRow = () => {
      if (activeRow > -1) {
        // @ts-ignore
        const value = Children.toArray(children)?.[activeRow]?.props?.value
        handleSelect(value)
      }
    }

    useEffect(() => {
      // если закрыт селект, нет value, но выбранна активная строка - сбрасываем такую строку
      if (!opened && activeRow !== -1 && !inputProps?.value) {
        setActiveRow(-1)
      }
    }, [activeRow, inputProps?.value, opened])

    const listVisible =
      opened &&
      !isDisabled &&
      children &&
      Children.toArray(children).filter(isValidElement).length > 0

    return (
      <Wrapper
        opened={opened}
        setOpen={open}
        onSelectActiveRow={handleSelectActiveRow}
        items={children}
        setActiveRow={setActiveRow}
      >
        <Input
          {...inputProps}
          opened={opened}
          setOpen={open}
          ref={ref}
          arrowTestId={arrowTestId}
        />
        {listVisible && (
          <List
            listStyle={listStyle}
            onSelect={handleSelect}
            activeItemIndex={activeRow}
            direction={direction}
            size={inputProps.size}
            listTestId={listTestId}
            listContentTestId={listContentTestId}
          >
            {children}
          </List>
        )}
      </Wrapper>
    )
  }
)

export default Select

export { Option } from './Option'
export * from './_methods'
